import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
  Storefront as StorefrontIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Group as GroupIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Work as WorkIcon,
  Task as TaskIcon,
  Analytics as AnalyticsIcon,
  SupportAgent as SupportAgentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Inbox as InboxIcon,
  Assignment as TaskAssignmentIcon,
  Phone as PhoneIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  LocationCity as LocationCityIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// Import upazila admin dashboard pages (you'll need to create these)
import UpazilaAdminTasks from './Tasks';
import UpazilaAdminSettings from './Settings';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2e7d32 30%, #388e3c 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const UpazilaAdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setCurrentTab(newValue);
    setMobileDrawerOpen(false);
  };

  // Translations for upazila admin dashboard
  const translations = {
    bn: {
      title: 'উপজেলা প্রশাসক ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      profile: 'প্রোফাইল',
      tasks: 'কাজসমূহ',
      categories: 'ক্যাটাগরিসমূহ',
      products: 'প্রডাক্ট লাইব্রেরী',
      orders: 'অর্ডার পরিচালনা',
      support: 'কাস্টমার সাপোর্ট',
      schedule: 'সময়সূচী',
      reports: 'রিপোর্ট',
      settings: 'সেটিংস',
      logout: 'লগআউট',
      myProfile: 'আমার প্রোফাইল',
      myTasks: 'আমার কাজ',
      todayTasks: 'আজকের কাজ',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English',
      upazilaManagement: 'উপজেলা ব্যবস্থাপনা',
      storeApproval: 'দোকান অনুমোদন',
      userManagement: 'ব্যবহারকারী ব্যবস্থাপনা',
      securityMonitoring: 'নিরাপত্তা পর্যবেক্ষণ'
    },
    en: {
      title: 'Upazila Admin Dashboard',
      dashboard: 'Dashboard',
      profile: 'Profile',
      tasks: 'Tasks',
      categories: 'Categories',
      products: 'Product Library',
      orders: 'Order Management',
      support: 'Customer Support',
      schedule: 'Schedule',
      reports: 'Reports',
      settings: 'Settings',
      logout: 'Logout',
      myProfile: 'My Profile',
      myTasks: 'My Tasks',
      todayTasks: 'Today\'s Tasks',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English',
      upazilaManagement: 'Upazila Management',
      storeApproval: 'Store Approval',
      userManagement: 'User Management',
      securityMonitoring: 'Security Monitoring'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/upazila-admin/dashboard' },
    { text: t.profile, icon: <PersonIcon />, path: '/upazila-admin/dashboard/profile' },
    { text: t.tasks, icon: <TaskIcon />, path: '/upazila-admin/dashboard/tasks' },
    { text: t.categories, icon: <CategoryIcon />, path: '/upazila-admin/dashboard/categories' },
    { text: t.products, icon: <InventoryIcon />, path: '/upazila-admin/dashboard/products' },
    { text: t.orders, icon: <ShoppingCartIcon />, path: '/upazila-admin/dashboard/orders' },
    { text: t.support, icon: <SupportAgentIcon />, path: '/upazila-admin/dashboard/support' },
    { text: t.schedule, icon: <ScheduleIcon />, path: '/upazila-admin/dashboard/schedule' },
    { text: t.reports, icon: <BarChartIcon />, path: '/upazila-admin/dashboard/reports' },
    { text: t.settings, icon: <SettingsIcon />, path: '/upazila-admin/dashboard/settings' },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => {
      return item.path === location.pathname;
    });
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Upazila Admin Action Buttons - Hidden on Mobile */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            gap: 1, 
            mr: 2 
          }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<VerifiedUserIcon />}
              component={Link}
              to="/upazila-admin/dashboard/store-approval"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              দোকান অনুমোদন
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              component={Link}
              to="/upazila-admin/dashboard/reports"
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              রিপোর্ট
            </Button>
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          {/* View Site Icon */}
          <IconButton 
            color="inherit" 
            component="a" 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <PublicIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={5} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>U</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.myProfile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                />
              ))}
            </Tabs>
          </Box>
        )}
      </AppBarStyled>

      {/* Mobile Navigation Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerClose}
        onOpen={() => setMobileDrawerOpen(true)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t.title}
            </Typography>
            <IconButton onClick={handleMobileDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        <List component="nav" sx={{ p: 1 }}>
          {menuItems.map((item, index) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleMobileNavigation(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Quick Actions in Mobile Drawer */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
            দ্রুত কার্যক্রম
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Store Approval Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<VerifiedUserIcon />}
              fullWidth
              component={Link}
              to="/upazila-admin/dashboard/store-approval"
              onClick={handleMobileDrawerClose}
              sx={{ 
                justifyContent: 'flex-start',
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              দোকান অনুমোদন
            </Button>
            
            {/* Report Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              fullWidth
              component={Link}
              to="/upazila-admin/dashboard/reports"
              onClick={handleMobileDrawerClose}
              sx={{ 
                justifyContent: 'flex-start',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'primary.light',
                  color: 'primary.dark'
                }
              }}
            >
              রিপোর্ট
            </Button>
            
            <Divider sx={{ my: 1 }} />
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<TaskAssignmentIcon />}
              fullWidth
              component={Link}
              to="/upazila-admin/dashboard/tasks"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              নতুন কাজ দেখুন
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<InventoryIcon />}
              fullWidth
              component={Link}
              to="/upazila-admin/dashboard/products"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              প্রডাক্ট যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              fullWidth
              component={Link}
              to="/upazila-admin/dashboard/reports"
              onClick={handleMobileDrawerClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              রিপোর্ট দেখুন
            </Button>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="menu"
          onClick={handleMobileDrawerToggle}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            display: { xs: 'flex', md: 'none' },
            background: 'linear-gradient(45deg, #2e7d32 30%, #388e3c 90%)'
          }}
        >
          <MenuIcon />
        </Fab>
      )}
      
      {/* Main Content Area with Sidebar */}
      <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
            {/* Quick Actions for Upazila Admin */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                background: 'linear-gradient(135deg, #2e7d32 30%, #388e3c 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              দ্রুত কার্যক্রম
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<VerifiedUserIcon />}
                fullWidth
                component={Link}
                to="/upazila-admin/dashboard/store-approval"
                sx={{ justifyContent: 'flex-start' }}
              >
                দোকান অনুমোদন
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AssessmentIcon />}
                fullWidth
                component={Link}
                to="/upazila-admin/dashboard/reports"
                sx={{ justifyContent: 'flex-start' }}
              >
                রিপোর্ট দেখুন
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<BarChartIcon />}
                fullWidth
                component={Link}
                to="/upazila-admin/dashboard/analytics"
                sx={{ justifyContent: 'flex-start' }}
              >
                বিশ্লেষণ দেখুন
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<InventoryIcon />}
                fullWidth
                component={Link}
                to="/upazila-admin/dashboard/products"
                sx={{ justifyContent: 'flex-start' }}
              >
                সব প্রডাক্ট দেখুন
              </Button>
            </Box>
            
            {/* Quick Stats */}
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              আজকের পরিসংখ্যান
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="body2" color="text.secondary">
                  অনুমোদনের অপেক্ষায়
                </Typography>
                <Typography variant="h4" color="primary">
                  ১২
                </Typography>
              </Paper>
              
              <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="body2" color="text.secondary">
                  আজকের অর্ডার
                </Typography>
                <Typography variant="h4" color="success.main">
                  ৪৫
                </Typography>
              </Paper>
              
              <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="body2" color="text.secondary">
                  নতুন দোকান
                </Typography>
                <Typography variant="h4" color="info.main">
                  ৩
                </Typography>
              </Paper>
            </Box>
            
            {/* Recent Notifications */}
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 3, 
                mb: 2, 
                fontWeight: 600,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              সাম্প্রতিক বিজ্ঞপ্তি
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  নতুন দোকান অনুমোদন
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ২টি দোকান অনুমোদনের অপেক্ষায়
                </Typography>
              </Paper>
              
              <Paper sx={{ p: 2, bgcolor: '#e8f5e8' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  সাপ্তাহিক রিপোর্ট
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  রিপোর্ট প্রস্তুত
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tasks" element={<UpazilaAdminTasks />} />
            <Route path="/settings" element={<UpazilaAdminSettings />} />
            {/* Add more routes for upazila admin pages when they are created
            <Route path="/profile" element={<UpazilaAdminProfile />} />
            <Route path="/categories" element={<UpazilaAdminCategories />} />
            <Route path="/products" element={<UpazilaAdminProducts />} />
            <Route path="/orders" element={<UpazilaAdminOrders />} />
            <Route path="/support" element={<UpazilaAdminSupport />} />
            <Route path="/schedule" element={<UpazilaAdminSchedule />} />
            <Route path="/reports" element={<UpazilaAdminReports />} />
            */}
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

// Upazila Admin Dashboard Home Component
const Dashboard = () => {
  // Mock data with upazila admin-relevant content
  const pendingApprovals = [
    { id: 1, storeName: 'তাজা সবজি দোকান', owner: 'আহমেদ হাসান', type: 'খাদ্যশস্য', status: 'অপেক্ষমান', submittedAt: 'আজ ৯:১৫ AM' },
    { id: 2, storeName: 'মাংস প্যারাডাইজ', owner: 'ফাতেমা খাতুন', type: 'মাংস', status: 'পর্যালোচনা', submittedAt: 'আজ ১০:৩০ AM' },
    { id: 3, storeName: 'ফলের রাজ্য', owner: 'করিম উদ্দিন', type: 'ফল', status: 'অপেক্ষমান', submittedAt: 'আজ ১১:৪৫ AM' },
  ];

  const todayOrders = [
    { id: 1, orderId: '#ORD-1001', customer: 'আহমেদ হাসান', store: 'তাজা সবজি দোকান', status: 'প্রসেসিং', amount: 850, time: '৯:১৫ AM' },
    { id: 2, orderId: '#ORD-1002', customer: 'ফাতেমা খাতুন', store: 'মাংস প্যারাডাইজ', status: 'নিশ্চিত', amount: 1200, time: '১০:৩০ AM' },
    { id: 3, orderId: '#ORD-1003', customer: 'করিম উদ্দিন', store: 'ফলের রাজ্য', status: 'প্যাকিং', amount: 650, time: '১১:৪৫ AM' },
  ];

  const upazilaStats = [
    { id: 1, metric: 'মোট দোকান', value: 156, change: '+12', changeType: 'positive' },
    { id: 2, metric: 'মোট বিক্রেতা', value: 89, change: '+8', changeType: 'positive' },
    { id: 3, metric: 'মাসিক বিক্রয়', value: '৳25L', change: '+15%', changeType: 'positive' },
    { id: 4, metric: 'সক্রিয় অর্ডার', value: 45, change: '-5', changeType: 'negative' },
    { id: 5, metric: 'নতুন ব্যবহারকারী', value: 23, change: '+7', changeType: 'positive' },
    { id: 6, metric: 'সাপোর্ট টিকেট', value: 8, change: '-2', changeType: 'negative' },
  ];

  return (
    <Box>
      {/* First Div - Upazila Admin Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {/* Enhanced Statistics Cards */}
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <StoreIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট দোকান
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[0].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট বিক্রেতা
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[1].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মাসিক বিক্রয়
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[2].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    সক্রিয় অর্ডার
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[3].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <GroupIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    নতুন ব্যবহারকারী
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[4].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <SupportAgentIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    সাপোর্ট টিকেট
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {upazilaStats[5].value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Second Div - Main Content Sections for Upazila Admin */}
      <Box>
        <Grid container spacing={2}>
          {/* Pending Store Approvals */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  দোকান অনুমোদনের অপেক্ষায়
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<VerifiedUserIcon />}
                  component={Link}
                  to="/upazila-admin/dashboard/store-approval"
                >
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>দোকানের নাম</TableCell>
                      <TableCell>মালিক</TableCell>
                      <TableCell>ধরন</TableCell>
                      <TableCell align="right">অবস্থা</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((approval) => (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {approval.storeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {approval.submittedAt}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{approval.owner}</TableCell>
                        <TableCell>{approval.type}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={approval.status} 
                            color={
                              approval.status === 'অপেক্ষমান' ? 'warning' :
                              approval.status === 'পর্যালোচনা' ? 'info' : 'default'
                            } 
                            size="small" 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Today's Orders */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  আজকের অর্ডার
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  component={Link}
                  to="/upazila-admin/dashboard/orders"
                >
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>অর্ডার আইডি</TableCell>
                      <TableCell>গ্রাহক</TableCell>
                      <TableCell>দোকান</TableCell>
                      <TableCell align="right">পরিমাণ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.orderId}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.customer}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {order.time}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{order.store}</TableCell>
                        <TableCell align="right">৳{order.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Upazila Overview */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                উপজেলা সংক্ষিপ্ত বিবরণ
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper sx={{ p: 2, bgcolor: '#e8f5e8', border: '1px solid #4caf50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        নতুন দোকান অনুমোদন
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        ২টি দোকান অনুমোদনের অপেক্ষায়
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="success">
                      অনুমোদন করুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        সাপ্তাহিক রিপোর্ট
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        রিপোর্ট প্রস্তুত
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="warning">
                      দেখুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        নিরাপত্তা পর্যবেক্ষণ
                      </Typography>
                      <Typography variant="body2" color="info.main">
                        সব ঠিক আছে
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="info">
                      চেক করুন
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { Dashboard as default, UpazilaAdminDashboard };
